import { memo, useState } from "react";
import { Tag } from "lucide-react";
import { cn } from "@repo/design-system/utils";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { formatPrice } from "@/utils/money";
import { SellerPromoCode } from "./SellerPromoCode";
import type { StoreSellerCart } from "./types";

export interface SellerSectionProps {
  /** The seller cart to display */
  cart: StoreSellerCart;
  /** Whether editing is disabled */
  disabled?: boolean;
  /** Callback when promo code is applied */
  onApplyPromo?: (cartId: string, code: string) => Promise<void>;
  /** Callback when promo code is removed */
  onRemovePromo?: (cartId: string) => Promise<void>;
  /** Currently applied promo code for this cart */
  appliedPromoCode?: string;
  /** Promo code error message */
  promoError?: string;
  /**
   * Whether to show the per-seller promo input in the summary.
   *
   * Cart keeps promo on each seller card's footer (KAN-75), so the cart
   * summary is a pure money roll-up with no promo. Checkout has no seller
   * cards, so the summary is where its per-seller promo lives - passing this
   * in checkout mode keeps that affordance exactly as it was.
   */
  showPromoCode?: boolean;
  /** Optional className */
  className?: string;
}

/**
 * One supplier's line in the order-summary roll-up: the seller name and its
 * cart subtotal, and nothing else. The item list, per-item quantities and the
 * per-seller breakdown that used to live here are gone - the left column owns
 * the products, the summary owns the money (KAN-70). The free-shipping meter
 * and (in cart mode) the promo moved to the seller card footer (KAN-75).
 */
export const SellerSection = memo(function SellerSection({
  cart,
  disabled = false,
  onApplyPromo,
  onRemovePromo,
  appliedPromoCode,
  promoError,
  showPromoCode = false,
  className
}: SellerSectionProps) {
  const sellerName = cart.seller?.name || "Unknown Seller";
  const showPromo = showPromoCode && onApplyPromo && onRemovePromo;
  const [promoOpen, setPromoOpen] = useState(false);
  // Open while the buyer wants it, while there is an error to show, or while a
  // code is applied (the applied chip sits in the same slot).
  const promoVisible = promoOpen || !!promoError || !!appliedPromoCode;

  return (
    <div className={cn("py-3", className)}>
      {/* One row per supplier: its name and its subtotal. A genuine summary -
          no thumbnails, no quantities, no second copy of the cart. */}
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm text-gray-600">{sellerName}</span>
          {/* Beside the supplier rather than the amount: this row is the one
              place the summary names a supplier, so it is where "this one's
              numbers came back live" belongs (KAN-105). */}
          <LiveProvenanceBadge
            provenance={cart.totals.provenance}
            subject="total"
            // Not the "Unknown Seller" fallback above - the badge has its own
            // ("the supplier"), which reads better than naming nobody twice.
            source={cart.seller?.name}
            className="flex-shrink-0"
          />
        </span>
        <span className="flex flex-shrink-0 items-center gap-2">
          {showPromo && !appliedPromoCode && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setPromoOpen((open) => !open)}
                    disabled={disabled}
                    aria-label="Add promo code"
                    aria-expanded={promoVisible}
                    className={cn(
                      "rounded p-1 text-gray-400 transition-colors",
                      "hover:bg-gray-100 hover:text-gray-700",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500",
                      "disabled:cursor-not-allowed disabled:opacity-50"
                    )}
                  >
                    <Tag className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">Add promo code</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className="text-sm font-medium tabular-nums text-gray-900">
            {formatPrice(cart.totals.total)}
          </span>
        </span>
      </div>

      {showPromo && promoVisible && (
        <div className="mt-2">
          <SellerPromoCode
            cartId={cart.cart_id}
            appliedCode={appliedPromoCode}
            onApply={onApplyPromo}
            onRemove={onRemovePromo}
            disabled={disabled}
            error={promoError}
            expanded={promoOpen}
            compact
          />
        </div>
      )}
    </div>
  );
});
